import React, { useState, useEffect, useCallback } from 'react';
import { Loading, Title } from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Paper, TextField, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import GetAppIcon from '@material-ui/icons/GetApp';
import dataProvider from '../../../providers/dataProvider';
import { getUserTokenId } from '../../../providers/firebaseConfig';
import ScreenDescription from '../ScreenDescription';

const API_URL = process.env.REACT_APP_API_URL;

const defaultFrom = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const defaultTo = () => new Date().toISOString().slice(0, 10);

// 4 casas decimais de propósito: custo por mensagem costuma ser fração de
// centavo de dólar (ex: US$ 0.003), e mostrar só 2 casas faz cada linha
// arredondar de forma independente — a soma dos filhos visualmente "não
// bate" com o total do bloco mesmo o cálculo estando certo. Com mais casas
// isso some.
const money = (value) => `US$ ${Number(value || 0).toFixed(4)}`;

// Uma linha de fonte (template/mensagem livre) já traz block/blockName/messageTypeName —
// aqui a gente só agrupa em memória por bloco (e dentro dele, por tipo), então
// um fetch só (nível "source") serve pros três níveis da tela: consolidado
// por bloco, quebra por tipo dentro do bloco, e granular por fonte.
const groupByBlock = (blocks, sourceRows) => {
  const byBlockId = {};
  blocks.forEach(b => {
    byBlockId[b.id] = { blockId: b.id, blockName: b.name, sentCount: 0, receivedCount: 0, totalCost: 0, types: new Set(), byTypeId: {} };
  });

  sourceRows.forEach(row => {
    let group = byBlockId[row.blockId];
    if (!group) {
      // fonte de um bloco que não veio na lista de blocos (ex: bloco desativado) — cria on the fly
      group = { blockId: row.blockId, blockName: row.blockName, sentCount: 0, receivedCount: 0, totalCost: 0, types: new Set(), byTypeId: {} };
      byBlockId[row.blockId] = group;
    }
    group.sentCount += row.sentCount;
    group.receivedCount += row.receivedCount;
    group.totalCost += row.totalCost;
    if (row.messageTypeName) group.types.add(row.messageTypeName);

    const typeKey = row.messageTypeId || 'none';
    if (!group.byTypeId[typeKey]) {
      group.byTypeId[typeKey] = {
        typeId: typeKey,
        typeName: row.messageTypeName || 'Sem tipo',
        sentCount: 0,
        receivedCount: 0,
        totalCost: 0,
        sources: [],
      };
    }
    const typeGroup = group.byTypeId[typeKey];
    typeGroup.sentCount += row.sentCount;
    typeGroup.receivedCount += row.receivedCount;
    typeGroup.totalCost += row.totalCost;
    typeGroup.sources.push(row);
  });

  return Object.values(byBlockId).map(group => ({
    ...group,
    typeName: group.types.size === 1 ? [...group.types][0] : '—',
    byType: Object.values(group.byTypeId).sort((a, b) => b.totalCost - a.totalCost),
  }));
};

const headCellStyle = { color: '#8a93a3', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' };
const numericStyle = { textAlign: 'right' };

// Tela principal de volumetria: card "Por bloco" com expand/collapse inline —
// clicar num bloco revela os templates/mensagens livres que o compõem, cada
// um com botão de reclassificar. Não é um Resource react-admin — é um
// dashboard custom, registrado como customRoute.
const VolumetryPage = () => {
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(defaultTo());
  const [blockGroups, setBlockGroups] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [blocksResult, sourcesResult] = await Promise.all([
        dataProvider('GET_LIST', 'whatsapp_block', { filter: { active: true }, pagination: {}, sort: {} }),
        dataProvider('GET_LIST', 'whatsapp_volumetry', { filter: { from, to, level: 'source' }, pagination: {}, sort: {} }),
      ]);
      setBlockGroups(groupByBlock(blocksResult.data, sourcesResult.data));
    } catch (err) {
      alert('Não foi possível carregar a volumetria.');
    }
    setLoading(false);
  }, [from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleBlock = (blockId) => {
    setExpanded({ ...expanded, [blockId]: !expanded[blockId] });
  };

  const exportCsv = async () => {
    try {
      const token = await getUserTokenId();
      const params = new URLSearchParams({ from, to, level: 'source' });
      const response = await fetch(`${API_URL}/whatsapp_volumetry/csv?${params.toString()}`, {
        headers: { Authorization: `${token}` },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `volumetria_whatsapp_${from}_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Não foi possível exportar o CSV.');
    }
  };

  const totals = blockGroups.reduce(
    (acc, g) => ({
      sentCount: acc.sentCount + g.sentCount,
      receivedCount: acc.receivedCount + g.receivedCount,
      totalCost: acc.totalCost + g.totalCost,
    }),
    { sentCount: 0, receivedCount: 0, totalCost: 0 }
  );

  return (
    <div style={{ padding: 24 }}>
      <Title title="Volumetria de mensagens WhatsApp" />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <ScreenDescription>
          Enviadas, recebidas e custo de WhatsApp no período escolhido. Clique num bloco pra expandir em três níveis: o total do
          bloco, a quebra por tipo (marketing/utilidade/autenticação/serviço/janela livre) dentro dele, e os templates/mensagens
          livres individuais de cada tipo — com botão pra reclassificar. O custo é sempre calculado com o valor vigente na data de
          cada mensagem — reclassificar uma fonte ou editar um custo depois não altera o que já foi fechado.
        </ScreenDescription>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap' }}>
        <TextField
          label="De"
          type="date"
          value={from}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Até"
          type="date"
          value={to}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="outlined" color="primary" onClick={exportCsv} style={{ marginBottom: 4 }}>
          <GetAppIcon style={{ marginRight: 8, fontSize: 18 }} />
          Exportar CSV
        </Button>
      </div>

      {loading && <Loading />}

      {!loading && (
        <Paper style={{ padding: '20px 24px' }} elevation={1}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500 }}>Por bloco</h2>
            <span style={{ color: '#999', fontSize: 13 }}>Clique em um bloco para ver os templates</span>
          </div>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={headCellStyle}>Bloco</TableCell>
                <TableCell style={headCellStyle}>Tipo</TableCell>
                <TableCell style={{ ...headCellStyle, ...numericStyle }}>Enviadas</TableCell>
                <TableCell style={{ ...headCellStyle, ...numericStyle }}>Recebidas</TableCell>
                <TableCell style={{ ...headCellStyle, ...numericStyle }}>Custo</TableCell>
                <TableCell style={headCellStyle} />
              </TableRow>
            </TableHead>
            <TableBody>
              {blockGroups.map(group => (
                <React.Fragment key={group.blockId}>
                  <TableRow hover onClick={() => toggleBlock(group.blockId)} style={{ cursor: 'pointer' }}>
                    <TableCell style={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton size="small" style={{ padding: 2, marginRight: 4 }}>
                        {expanded[group.blockId] ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                      </IconButton>
                      {group.blockName}
                    </TableCell>
                    <TableCell>{group.typeName}</TableCell>
                    <TableCell style={numericStyle}>{group.sentCount}</TableCell>
                    <TableCell style={numericStyle}>{group.receivedCount}</TableCell>
                    <TableCell style={numericStyle}>{money(group.totalCost)}</TableCell>
                    <TableCell />
                  </TableRow>
                  {expanded[group.blockId] && group.byType.map(typeGroup => (
                    <React.Fragment key={`${group.blockId}-${typeGroup.typeId}`}>
                      <TableRow style={{ background: '#f2f2f2' }}>
                        <TableCell style={{ paddingLeft: 32, fontWeight: 500 }}>{typeGroup.typeName}</TableCell>
                        <TableCell />
                        <TableCell style={numericStyle}>{typeGroup.sentCount}</TableCell>
                        <TableCell style={numericStyle}>{typeGroup.receivedCount}</TableCell>
                        <TableCell style={numericStyle}>{money(typeGroup.totalCost)}</TableCell>
                        <TableCell />
                      </TableRow>
                      {typeGroup.sources.map(source => (
                        <TableRow key={source.sourceId} style={{ background: '#fafafa' }}>
                          <TableCell style={{ paddingLeft: 64, fontFamily: 'monospace', fontSize: 13, color: '#555' }}>
                            {source.sourceName}
                          </TableCell>
                          <TableCell />
                          <TableCell style={numericStyle}>{source.sentCount}</TableCell>
                          <TableCell style={numericStyle}>{source.receivedCount}</TableCell>
                          <TableCell style={numericStyle}>{money(source.totalCost)}</TableCell>
                          <TableCell style={numericStyle}>
                            <Button
                              size="small"
                              variant="outlined"
                              component={Link}
                              to={`/whatsapp_message_source/${source.sourceId}`}
                              style={{ borderRadius: 999, textTransform: 'none' }}
                            >
                              Reclassificar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                  {expanded[group.blockId] && group.byType.length === 0 && (
                    <TableRow style={{ background: '#fafafa' }}>
                      <TableCell colSpan={6} style={{ paddingLeft: 48, color: '#999' }}>
                        Nenhuma mensagem nesse bloco no período.
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              <TableRow>
                <TableCell style={{ fontWeight: 'bold', borderTop: '2px solid #ddd' }}>Total</TableCell>
                <TableCell style={{ borderTop: '2px solid #ddd' }} />
                <TableCell style={{ ...numericStyle, fontWeight: 'bold', borderTop: '2px solid #ddd' }}>{totals.sentCount}</TableCell>
                <TableCell style={{ ...numericStyle, fontWeight: 'bold', borderTop: '2px solid #ddd' }}>{totals.receivedCount}</TableCell>
                <TableCell style={{ ...numericStyle, fontWeight: 'bold', borderTop: '2px solid #ddd' }}>{money(totals.totalCost)}</TableCell>
                <TableCell style={{ borderTop: '2px solid #ddd' }} />
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}
    </div>
  );
};

export default VolumetryPage;
